import { Cesium } from 'app/cesium';
import { ConfigService } from 'app/config/config.service';
import { AsyncPrimitive } from 'app/map/geometry/cesium/AsyncPrimitive/AsyncUpdatablePrimitive';
import {
    createPolylineAsyncPrimitive,
    DegreesToMetersApprox,
    PolylineAsyncPrimitiveAttributes,
} from 'app/map/geometry/cesium/AsyncPrimitive/utils';
import { CesiumTransientLabel } from 'app/map/geometry/cesium/CesiumTransientLabel';
import { CesiumZone } from 'app/map/geometry/cesium/CesiumZones/CesiumZone';
import { ImageUrlService } from 'app/shared/image_url.service';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { cartesian3ToDegrees } from 'app/shared/utilities/mapping_utils';
import {
    ConnectionLineModel,
    IconIndicatorModel,
    LabelModel,
    LineModel,
    PolygonModel,
    VectorModel,
    ViewComponentModel,
} from 'app/views/accessories/IndicatorManager';
import { last } from 'lodash';
import { OffsetManager } from './OffsetManager';
import { AssetTypeCollection } from 'app/shared/collections/asset_type_collection.service';
import { AssetType } from 'app/shared/collections/asset_type';
import polylabel from 'polylabel';
import { isEntityType } from 'app/agriculture/models/Entities';
import { getAssetIconConfig } from 'app/agriculture/icons/utils/map-icon-utils/getAssetIconConfig';
import { createOverlayIconDataUri } from 'app/agriculture/icons/utils/map-icon-utils/createOverlayIconDataUri';

const ASSET_PIXEL_SIZE = ConfigService.cesiumAssetPixelSize;
const INDICATOR_OFFSET_SIZE = 50;
const ASSET_AS_INDICATOR_OFFSET_SIZE = 60;

function defaultLabel(): Cesium.Label {
    const l = {} as Cesium.Label;
    l.font = `600 ${ConfigService.cesiumAssetLabelPixelSize}px Helvetica`;
    l.scale = 1;
    l.fillColor = Cesium.Color.WHITE;
    l.style = Cesium.LabelStyle.FILL_AND_OUTLINE;
    // TODO constify these scalars/cartesian2?
    l.translucencyByDistance = new Cesium.NearFarScalar(1.5e3, 1.0, 1.5e6, 0.0);
    // The strange scaling value here comes from when the asset pixel size was hard set to
    // 36. By providing this scaling factor we can now adapt to different pixel sizes.
    l.pixelOffset = new Cesium.Cartesian2(0, ASSET_PIXEL_SIZE * (8 / 9)); // x, y
    l.horizontalOrigin = Cesium.HorizontalOrigin.CENTER;
    return l;
}

function copyLabel(currentLabel: Cesium.Label): Cesium.Label {
    const l = defaultLabel();
    const cl = currentLabel;

    l.text = cl.text;
    l.show = cl.show;

    l.font = cl.font;
    l.fillColor = cl.fillColor;
    l.style = cl.style;
    l.pixelOffset = cl.pixelOffset;
    l.verticalOrigin = cl.verticalOrigin;
    l.pixelOffsetScaleByDistance = cl.pixelOffsetScaleByDistance;
    l.scaleByDistance = cl.scaleByDistance;
    l.disableDepthTestDistance = cl.disableDepthTestDistance;
    l.heightReference = cl.heightReference;

    l.eyeOffset = cl.eyeOffset;
    l.position = cl.position;
    return l;
}

const INDICATOR_BILLBOARD_TRANSLUCENCY_SCALAR = new Cesium.NearFarScalar(1.5e4, 1.0, 1.5e6, 0.0);
const INDICATOR_SCALAR = new Cesium.NearFarScalar(1.5e3, 1.0, 1.5e6, 0.0);

const DEFAULT_SCALAR = new Cesium.NearFarScalar(1e4, 1.0, 3e6, 0.0);

function getScaleByDistance() {
    return ConfigService.globalFarmMode ? undefined : DEFAULT_SCALAR;
}

const isLabel = (item): item is LabelModel => item.type === 'label';
const isVector = (item): item is VectorModel => item.type === 'vector';
const isConnectionLines = (item): item is ConnectionLineModel => item.type === 'connection-line';
const isIconIndicator = (item): item is IconIndicatorModel => item.type === 'indicator';
const isPolygon = (item): item is PolygonModel => item.type === 'polygon';
const isLine = (item): item is LineModel => item.type === 'linestring';

function defaultAssetIcon(): Cesium.Billboard {
    const b = {} as Cesium.Billboard;
    b.color = Cesium.Color.WHITE;
    b.width = ASSET_PIXEL_SIZE;
    b.height = b.width;
    b.scaleByDistance = getScaleByDistance();
    return b;
}

export interface CesiumPolylineOptions {
    show?: boolean;
    width?: number;
    loop?: boolean;
    material?: Cesium.Material;
    positions?: Cesium.Cartesian3[];
    id?: any;
}

function defaultHeightPole(): CesiumPolylineOptions {
    return {
        material: new Cesium.Material.fromType('PolylineOutline', {
            color: new Cesium.Color(1, 1, 1, 0.2),
            outlineColor: Cesium.Color.TRANSPARENT,
            outlineWidth: 0,
        }),
        width: 3,
        positions: [],
    };
}

function defaultSelection(imageUrlService: ImageUrlService): Cesium.Billboard {
    const b = {} as Cesium.Billboard;
    b.width = ASSET_PIXEL_SIZE + 9;
    b.height = b.width;
    b.image = imageUrlService.getUrl('selection', undefined, undefined);
    b.scaleByDistance = getScaleByDistance();
    return b;
}

export function copyBillboard(currentBillboard: Cesium.Billboard): Cesium.Billboard {
    const b = {} as Cesium.Billboard;
    b.color = currentBillboard.color;
    b.width = currentBillboard.width;
    b.height = currentBillboard.height;
    b.scaleByDistance = currentBillboard.scaleByDistance;
    b.image = currentBillboard.image;
    //
    b.eyeOffset = currentBillboard.eyeOffset;
    b.position = currentBillboard.position;
    b.show = currentBillboard.show;
    return b;
}

function defaultPolygonFactory(
    labelCollection: Cesium.LabelCollection,
    primitiveCollection: Cesium.PrimitiveCollection,
    allowSelection = true,
    id: number,
): CesiumZone {
    return new CesiumZone(
        id,
        labelCollection,
        primitiveCollection,
        allowSelection,
        SelectionType.ASSET,
    );
}

interface CesiumIndicator {
    label: CesiumTransientLabel;
    billboard: Cesium.Billboard;
    isSelected: boolean;
}

interface CesiumVector {
    heading: number;
    pitch: number;
    length: number;
    polyline: Cesium.Polyline;
}

interface CesiumLabel {
    label: CesiumTransientLabel;
}

interface CesiumConnectionLine {
    polyline: Cesium.Polyline;
}

interface CesiumLine {
    primitive: AsyncPrimitive<PolylineAsyncPrimitiveAttributes>;
}

interface CesiumPolygon {
    zone: CesiumZone;
}

// CesiumAsset is a convenience wrapper for the primitives in cesium that make up an asset.
export class CesiumAsset {
    private _id: number;
    // State
    private _isSelected: boolean;
    private _isVisible: boolean;
    private _showLabel: boolean;
    private _position: Cesium.Cartesian3;
    private _alpha: number;

    // Cesium Collections
    private _assetIconCollection: Cesium.BillboardCollection;
    private _selectionCollection: Cesium.BillboardCollection;
    private _labelCollection: Cesium.LabelCollection;
    // Helper Services
    private _offsetManager: OffsetManager;
    private _imageUrlService: ImageUrlService;

    // Cesium Geometries, left public for reference by connections/indicators
    label: Cesium.Label;
    assetIcon: Cesium.Billboard;
    _indicators: Array<CesiumIndicator>;
    _vectors: Array<CesiumVector>;
    _labels: Array<CesiumLabel>;
    _connectionLines: Array<CesiumConnectionLine>;
    _lines: Array<CesiumLine>;
    _polygons: Array<CesiumPolygon>;
    private readonly heightPole: Cesium.Polyline;
    private readonly assetType: AssetType;

    selection: Cesium.Billboard;

    private readonly _indicatorIconUrl: string;
    private readonly _polylineCollection: Cesium.PolylineCollection;
    private readonly _extraLabelCollection: Cesium.LabelCollection;
    private readonly _polygonPrimitiveCollection: Cesium.PrimitiveCollection;
    private readonly _getPositionAndShowForAsset: (id: number) => {
        position: Cesium.Cartesian3;
        isShowingIcon: boolean;
    };
    // only exposed for testing
    private readonly _zoneFactory: (
        labelCollection: Cesium.LabelCollection,
        primitiveCollection: Cesium.PrimitiveCollection,
        allowSelection: boolean,
        id: number,
    ) => CesiumZone;

    constructor(
        id: number,
        assetTypeId: number,
        assetIconCollection: Cesium.BillboardCollection,
        selectionCollection: Cesium.BillboardCollection,
        polylineCollection: Cesium.PolylineCollection,
        labelCollection: Cesium.LabelCollection,
        extraLabelCollection: Cesium.LabelCollection,
        polygonPrimitiveCollection: Cesium.PrimitiveCollection,
        imageUrlService: ImageUrlService,
        offsetManager: OffsetManager,
        getPositionAndShowForAsset: (id: number) => {
            position: Cesium.Cartesian3;
            isShowingIcon: boolean;
        },
        assetTypeCollection: AssetTypeCollection,
        zoneFactory: (
            labelCollection: Cesium.LabelCollection,
            primitiveCollection: Cesium.PrimitiveCollection,
            allowSelection: boolean,
            id: number,
        ) => CesiumZone = defaultPolygonFactory,
    ) {
        this._id = id;
        this._getPositionAndShowForAsset = getPositionAndShowForAsset;
        this._zoneFactory = zoneFactory;
        // Asset representation
        this.label = defaultLabel();
        this.assetIcon = defaultAssetIcon();
        this.selection = defaultSelection(imageUrlService);

        this._indicatorIconUrl = imageUrlService.getUrl('indicator', 'base_icon', 'png');
        this._indicators = [];
        this._vectors = [];
        this._connectionLines = [];
        this._polygons = [];
        this._lines = [];
        this._labels = [];

        // Add to cesium collections
        this._assetIconCollection = assetIconCollection;
        this._selectionCollection = selectionCollection;
        this._labelCollection = labelCollection;
        this._polylineCollection = polylineCollection;
        this._extraLabelCollection = extraLabelCollection;
        this._polygonPrimitiveCollection = polygonPrimitiveCollection;

        this._imageUrlService = imageUrlService;
        this.assetType = assetTypeCollection.get(assetTypeId);

        // Display services
        this._offsetManager = offsetManager;

        // Default State
        this.assetIcon.show = false;
        this.selection.show = false;
        this.label.show = false;
        this._position = undefined;
        this._alpha = 1;

        this._isVisible = this.assetIcon.show;
        this._isSelected = this.selection.show;
        this._showLabel = this.label.show;

        // Initialize
        this.assetIcon = this._assetIconCollection.add(this.assetIcon);
        this.heightPole = this._polylineCollection.add(defaultHeightPole());

        // Identifiers for selection service
        (this.assetIcon as any).getId = () => {
            return id;
        };
        (this.assetIcon as any).selectionType = SelectionType.ASSET;

        this._offsetManager.set(
            id, // id ref
            this, // primitive ref
            OffsetManager.ZINDEX.Asset,
        );
    }

    destroy() {
        this._offsetManager.delete(this.id);
        this._assetIconCollection.remove(this.assetIcon);
        this._selectionCollection.remove(this.selection);
        this._polylineCollection.remove(this.heightPole);
        this._labelCollection.remove(this.label);

        while (this._polygons.length) {
            dropPolygon(this._polygons);
        }

        while (this._labels.length) {
            dropLabel(this._labels);
        }

        while (this._vectors.length) {
            dropVector(this._vectors, this._polylineCollection);
        }

        while (this._connectionLines.length) {
            dropConnectionLine(this._connectionLines, this._polylineCollection);
        }

        while (this._lines.length) {
            dropLine(this._lines);
        }

        while (this._indicators.length) {
            dropIndicator(
                this.id,
                this._indicators,
                this._assetIconCollection,
                this._offsetManager,
            );
        }
    }

    get billboard() {
        throw Error('Who dare access this?');
    }

    get point() {
        throw Error('Who dare access this?');
    }

    get id() {
        return this._id;
    }

    set labelText(name) {
        this.label.text = name;
    }

    get labelText() {
        return this.label.text;
    }

    set alpha(alpha: number) {
        this._alpha = alpha;
    }

    get alpha() {
        return this._alpha;
    }

    public setPositions(position: Cesium.Cartesian3, basePosition: Cesium.Cartesian3): void {
        const oldPosition = this.position;
        if (this.assetType?.name == 'Herd' && !position) {
            // Herds don't have a default position, so if we ask for their position, and we get null, just ignore it
            return;
        }
        if (cesiumEqualityChecker(position, this.position)) {
            return;
        }
        this._position = position;
        if (!position) {
            this._manageAsset();
            return;
        }

        // If we are transitioning from no position to a position
        if (!oldPosition && position) {
            this._manageAsset();
        }

        this.label.position = position;
        this.assetIcon.position = position;

        if (basePosition) {
            this.heightPole.show = true;
            this.heightPole.positions = [basePosition, position];
        } else {
            this.heightPole.show = false;
            this.heightPole.positions = [];
        }

        this.selection.position = position;

        // update the location of all the indicators to match the asset location
        for (const { label, billboard } of this._indicators) {
            label.position = position as unknown as Cesium.PositionProperty;
            billboard.position = position;
        }

        // update the locations of all the vectors to match the asset location
        const tail = this.position;
        for (let i = 0; i < this._vectors.length; i++) {
            const { polyline, heading, pitch, length } = this._vectors[i];
            const head = createArrowHead(
                tail,
                Cesium.HeadingPitchRoll.fromDegrees(heading, pitch, 0),
                length,
            );
            polyline.positions = [tail, head];
        }

        // update the location of all the labels to match the asset location
        for (const { label } of this._labels) {
            label.position = position as unknown as Cesium.PositionProperty;
        }
    }

    get position(): Cesium.Cartesian3 {
        //HACK for the ag product, we want to position indicator components around the first "polygon" component. Ie; we want to show mob indicators around their assigned paddock (polygon)
        return !this._position && this._polygons && this._polygons[0]
            ? this._polygons[0].zone.labelPosition
            : this._position;
    }

    set image(image) {
        this.assetIcon.image = image;
    }

    set color(color: Cesium.Color) {
        // Remove this equality when we're not running per frame.
        // A successful change runs this three times.
        if (cesiumEqualityChecker(color, this.assetIcon.color)) {
            return;
        }

        this.assetIcon.color = color;
        this.selection.color = color;
        this.heightPole.material.uniforms.color = color;
    }

    set eyeOffset(eyeOffset: Cesium.Cartesian3) {
        this.assetIcon.eyeOffset = eyeOffset;
        // Commented this out as it breaks the selection ring being higher than anything else. Order for selections doesn't matter if it gets reshuffled anyway.
        // this.selection.eyeOffset = eyeOffset;
        this.label.eyeOffset = eyeOffset;
        for (const { label } of this._indicators) {
            // just copy the eye offset to all the indicators labels
            // the indicator billboard offset is calculated separately
            label.eyeOffset = getIndicatorLabelEyeOffset(eyeOffset);
        }

        for (const { label } of this._labels) {
            label.eyeOffset = getLabelEyeOffset(eyeOffset);
        }
    }

    public isVisible(): boolean {
        return this._isVisible;
    }

    public isSelected(): boolean {
        return this._isSelected;
    }

    public show(): void {
        if (this._isVisible) {
            return;
        }
        this._isVisible = true;
        this._manageAsset();
    }

    public hide(): void {
        if (!this._isVisible) {
            return;
        }
        this._isVisible = false;
        this._manageAsset();
    }

    public showLabel(): void {
        if (this._showLabel) {
            return;
        }

        this._showLabel = true;
        this._manageLabel();
    }

    public hideLabel(): void {
        if (!this._showLabel) {
            return;
        }

        this._showLabel = false;
        this._manageLabel();
    }

    public select(): void {
        if (this._isSelected) {
            return;
        }

        this._isSelected = true;
        this._manageSelection();
    }

    public deselect(): void {
        if (!this._isSelected) {
            return;
        }
        this._isSelected = false;
        this._manageSelection();
    }

    public isShowingIcon(): boolean {
        return !!this.position && this._isVisible;
    }

    private isShowingPolygon(): boolean {
        return this._isVisible;
    }

    private isShowingPolygonLabel(): boolean {
        return this.isShowingPolygon() && (this._showLabel || this.isSelected());
    }

    _manageAsset() {
        const shouldShowAssetIcon = this.isShowingIcon();
        this.assetIcon.show = shouldShowAssetIcon;
        const isHeightPolePositionValid = this.heightPole.positions.length === 2;
        this.heightPole.show = isHeightPolePositionValid && shouldShowAssetIcon;

        this._manageLabel();
        this._manageSelection();

        const showIndicatorLabel = shouldShowIndicatorLabel(shouldShowAssetIcon, this._isSelected);
        const showIndicatorBillboard = shouldShowIndicatorBillboard(shouldShowAssetIcon);

        for (const { label, billboard, isSelected } of this._indicators) {
            const showIndicatorLabel = shouldShowIndicatorLabel(
                shouldShowAssetIcon,
                isSelected || this._isSelected,
            );
            label.show = showIndicatorLabel;
            billboard.show = showIndicatorBillboard;
        }

        for (const vector of this._vectors) {
            vector.polyline.show = showIndicatorBillboard;
        }

        for (const { label } of this._labels) {
            label.show = showIndicatorLabel;
        }

        const showPolygonLabel = this.isShowingPolygonLabel();
        const showPolygon = this.isShowingPolygon();
        for (const { zone } of this._polygons) {
            zone.showPolygon = showPolygon;
            zone.showLabel = showPolygonLabel;
            zone.applyPendingRefreshes();
        }

        // note we're not updating connection line here
        //  because that's triggered by the cesium asset manager
        //  since we need to know when either the near or far side (asset) of the connection line
        //  is being shown/hidden
    }

    _manageLabel() {
        if ((!this._showLabel && !this._isSelected) || !this.isShowingIcon()) {
            // Destroy label if it's currently visible.
            if (this.label.show === true) {
                this._destroyLabel();
            }
            return;
        }

        // Create one if it's currently not visible
        if (this.label.show === false) {
            this._createLabel();
        }
    }

    _createLabel() {
        this.label.show = true;
        this.label = this._labelCollection.add(this.label);
        // allow labels to be seen behind terrain
        this.label.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    }

    _destroyLabel() {
        this.label.show = false;
        const newLabel = copyLabel(this.label);
        this._labelCollection.remove(this.label);
        this.label = newLabel;
    }

    _manageSelection() {
        // hide or show each indicator text if we're visible
        for (const { label, isSelected } of this._indicators) {
            label.show = shouldShowIndicatorLabel(
                this.isShowingIcon(),
                isSelected || this._isSelected,
            );
        }

        // Check if we need to redraw label
        this._manageLabel();
        if (!this._isSelected || !this.isShowingIcon()) {
            // Destroy selection if it's currently visible.
            if (this.selection.show === true) {
                this._destroySelection();
            }
            return;
        }
        // Create one if it's currently not visible.
        if (this.selection.show === false) {
            this._createSelection();
        }
    }

    public setExtraComponents(v: Array<ViewComponentModel>): void {
        // hohoho split this thing up into five arrays and forget that
        //  we ever slammed all the rest of them in here as well!
        const indicators: Array<IconIndicatorModel> = v.filter<IconIndicatorModel>(isIconIndicator);
        const vectors: Array<VectorModel> = v.filter<VectorModel>(isVector);
        const connectionLines: Array<ConnectionLineModel> =
            v.filter<ConnectionLineModel>(isConnectionLines);
        const labels: Array<LabelModel> = v.filter<LabelModel>(isLabel);
        const polygons: Array<PolygonModel> = v.filter<PolygonModel>(isPolygon);
        const lines: Array<LineModel> = v.filter<LineModel>(isLine);

        this._drawVectors(vectors);
        this._drawLabels(labels);
        this._drawConnectionLines(connectionLines);
        this._drawLines(lines);
        this._drawPolygons(polygons);
        this._drawIconIndicators(indicators);
    }

    private _drawIconIndicators(indicators: Array<IconIndicatorModel>): void {
        const recalculatePositions = indicators.length !== this._indicators.length;
        // remove any extraneous indicators
        while (this._indicators.length > indicators.length) {
            dropIndicator(
                this.id,
                this._indicators,
                this._assetIconCollection,
                this._offsetManager,
            );
        }
        const position = this.position;
        // create any missing indicators
        while (this._indicators.length < indicators.length) {
            const billboard = this._assetIconCollection.add({
                eyeOffset: getIndicatorBillboardEyeOffset(this.assetIcon.eyeOffset),
                show: shouldShowIndicatorBillboard(this._isVisible),
                width: BILLBOARD_WIDTH,
                height: BILLBOARD_HEIGHT,
                position,
                translucencyByDistance: INDICATOR_BILLBOARD_TRANSLUCENCY_SCALAR,
                scaleByDistance: INDICATOR_SCALAR,
                pixelOffsetScaleByDistance: INDICATOR_SCALAR,
            });
            const label = new CesiumTransientLabel(this._labelCollection, {
                eyeOffset: getIndicatorLabelEyeOffset(this.assetIcon.eyeOffset),
                font: `600 ${ConfigService.cesiumIndicatorLabelPixelSize}px Helvetica`,
                show: shouldShowIndicatorLabel(this.isShowingIcon(), this._isSelected),
                fillColor: Cesium.Color.WHITE,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                position: this.position as unknown as Cesium.PositionProperty,
                translucencyByDistance: INDICATOR_SCALAR,
                scaleByDistance: INDICATOR_SCALAR,
                pixelOffsetScaleByDistance: INDICATOR_SCALAR,
            });
            this._indicators.push({
                billboard,
                label,
                isSelected: false,
            });
            // a cheap hack to get unique id for each indicator
            const indicatorId = this.id + 0.01 * this._indicators.length;
            this._offsetManager.set(indicatorId, billboard, OffsetManager.ZINDEX.Indicator);
        }

        // recalculate the positions for the indicators
        const offsets = recalculatePositions
            ? calculatePositionsAroundCircle(indicators.length, 8)
            : undefined;

        // update our indicators
        for (let i = 0; i < this._indicators.length; i++) {
            const indicator = indicators[i];
            this._indicators[i].isSelected = indicator.isSelected;
            const { billboard, label } = this._indicators[i];
            label.text = indicator.text;
            billboard.image = indicator.icon;
            billboard.color = indicator.color;
            if (recalculatePositions) {
                const { radians, layer } = offsets[i];
                label.verticalOrigin = getVerticalOrigin(radians);
                label.horizontalOrigin = getHorizontalOrigin(radians);

                //Asset as Indicator
                if (indicator.asset) {
                    billboard.width = ASSET_PIXEL_SIZE;
                    billboard.height = ASSET_PIXEL_SIZE;
                    billboard.pixelOffset = getCustomSpacingOffset(
                        radians,
                        layer,
                        ASSET_PIXEL_SIZE,
                    );
                    label.pixelOffset = getLabelOffset(
                        radians,
                        layer,
                        ASSET_AS_INDICATOR_OFFSET_SIZE,
                    );

                    (billboard as any).getId = () => {
                        return indicator.asset.id;
                    };
                    (billboard as any).selectionType = SelectionType.ASSET;
                }
                //Standard Indicator
                else {
                    billboard.width = BILLBOARD_WIDTH;
                    billboard.height = BILLBOARD_HEIGHT;
                    billboard.pixelOffset = getBillboardOffset(radians, layer);

                    label.pixelOffset = getLabelOffset(radians, layer, INDICATOR_OFFSET_SIZE);

                    (billboard as any).getId = null;
                    (billboard as any).selectionType = null;
                }
            }
        }
    }

    private _drawLines(lines: Array<LineModel>): void {
        while (this._lines.length > lines.length) {
            dropLine(this._lines);
        }

        while (this._lines.length < lines.length) {
            this._lines.push({
                primitive: createPolylineAsyncPrimitive(
                    this._id,
                    this._polygonPrimitiveCollection,
                    true,
                ),
            });
        }

        const isVisible = this._isVisible;
        for (let i = 0; i < this._lines.length; i++) {
            const line = lines[i];
            const { primitive } = this._lines[i];
            primitive
                .setAttributes({
                    positions: line.line,
                    color: this.assetType.mapAppearanceLineColor
                        ? this.assetType.mapAppearanceLineColor.withAlpha(this._alpha)
                        : line.color.withAlpha(this._alpha),
                    show: isVisible,
                    width: this.assetType.mapAppearanceLineThickness ?? line.width,
                    zIndex: this.assetType.mapAppearanceZIndex ?? 0,
                })
                .catch((e) => console.error(e));
        }
    }

    private _drawConnectionLines(connectionLines: Array<ConnectionLineModel>): void {
        // remove any extraneous vectors
        while (this._connectionLines.length > connectionLines.length) {
            dropConnectionLine(this._connectionLines, this._polylineCollection);
        }
        // create any missing vectors
        while (this._connectionLines.length < connectionLines.length) {
            const polyline = this._polylineCollection.add({
                show: false,
                positions: [],
                width: 1,
                material: Cesium.Material.fromType(Cesium.Material.ColorType),
            });
            this._connectionLines.push({
                polyline,
            });
        }

        const isAssetShowing = this.isShowingIcon();
        const tail = this.position;
        const hasTailAndAssetIsShowing = !!tail && shouldShowIndicatorBillboard(isAssetShowing);

        for (let i = 0; i < this._connectionLines.length; i++) {
            const connectionLine = connectionLines[i];
            const cesiumConnectionLine = this._connectionLines[i];
            const { position: head, isShowingIcon: isHeadShown } = this._getPositionAndShowForAsset(
                connectionLine.destinationAssetId,
            );
            const show = hasTailAndAssetIsShowing && !!head && isHeadShown;

            cesiumConnectionLine.polyline.show = show;

            if (!show) {
                // no point updating the rest of the properties if we're hidden
                //  we'll just update them next time they'll be shown
                continue;
            }

            cesiumConnectionLine.polyline.material.uniforms.color = connectionLine.color;
            cesiumConnectionLine.polyline.width = connectionLine.width;
            cesiumConnectionLine.polyline.positions = [tail, head];
        }
    }

    private _drawPolygons(polygons: Array<PolygonModel>): void {
        while (this._polygons.length > polygons.length) {
            dropPolygon(this._polygons);
        }

        while (this._polygons.length < polygons.length) {
            const zone = this._zoneFactory(
                this._labelCollection,
                this._polygonPrimitiveCollection,
                true,
                this._id,
            );
            this._polygons.push({
                zone,
            });
        }

        const showPolygon = this.isShowingPolygon();
        const showPolygonLabel = this.isShowingPolygonLabel();
        for (let i = 0; i < this._polygons.length; i++) {
            const polygon = polygons[i];
            const cesiumPolygon = this._polygons[i];

            cesiumPolygon.zone.changePositionsIfNeeded(polygon.polygon);
            // Just check the asset is visible and to show the polygon - we're being handed the polygon area for the timestamp
            cesiumPolygon.zone.showPolygon = showPolygon;
            cesiumPolygon.zone.color = this.assetType.mapAppearanceFillColor;
            cesiumPolygon.zone.alpha = this.assetType.mapAppearanceFillAlpha * this._alpha;
            cesiumPolygon.zone.outlineWidth = this.assetType.mapAppearanceOutlineThickness;
            cesiumPolygon.zone.outlineColor =
                this.assetType.mapAppearanceOutlineColor &&
                this.assetType.mapAppearanceOutlineColor.withAlpha(this._alpha);
            cesiumPolygon.zone.zIndex = this.assetType.mapAppearanceZIndex ?? 0;

            const location = polylabel(
                [polygon.polygon.map(cartesian3ToDegrees).map((i) => [i.latitude, i.longitude])],
                DegreesToMetersApprox,
            );
            cesiumPolygon.zone.labelPosition = Cesium.Cartesian3.fromDegrees(
                location[1],
                location[0],
            );

            cesiumPolygon.zone.clampLabelToGround();
            cesiumPolygon.zone.setLabelCannotBeOccluded();
            cesiumPolygon.zone.showLabel = showPolygonLabel;
            cesiumPolygon.zone.labelText = this.label.text ? this.label.text : '';

            const assetTypeName = this.assetType.name;
            if (isEntityType(assetTypeName)) {
                const { icon, options } = getAssetIconConfig(assetTypeName);

                if (!icon) return;

                cesiumPolygon.zone.overlayImage = createOverlayIconDataUri(icon, options);
            }

            cesiumPolygon.zone.applyPendingRefreshes();
        }
    }

    private _drawVectors(vectors: Array<VectorModel>): void {
        // remove any extraneous vectors
        while (this._vectors.length > vectors.length) {
            dropVector(this._vectors, this._polylineCollection);
        }
        // create any missing vectors
        while (this._vectors.length < vectors.length) {
            const polyline = this._polylineCollection.add({
                show: false,
                positions: [],
                width: 1,
                material: Cesium.Material.fromType(Cesium.Material.PolylineArrowType, {
                    color: Cesium.Color.WHITE,
                }),
            });
            this._vectors.push({
                polyline,
                heading: 0,
                pitch: 0,
                length: 0,
            });
        }

        const isAssetShowing = this.isShowingIcon();
        const tail = this.position;
        const show = !!tail && shouldShowIndicatorBillboard(isAssetShowing);

        // update our vectors
        for (let i = 0; i < this._vectors.length; i++) {
            const vector = vectors[i];
            const cesiumVector = this._vectors[i];
            cesiumVector.polyline.show = show;
            cesiumVector.polyline.material.uniforms.color = vector.color;
            cesiumVector.polyline.width = vector.width;
            // make sure we update these fields as well so they can be referenced later
            //  to recalculate the arrow locations
            cesiumVector.heading = vector.heading;
            cesiumVector.pitch = vector.pitch;
            cesiumVector.length = vector.length;
            if (!show) {
                // no point updating the rest of the properties if we're hidden
                //  we'll just update them next time they'll be shown
                continue;
            }
            const head = createArrowHead(
                tail,
                Cesium.HeadingPitchRoll.fromDegrees(vector.heading, vector.pitch, 0),
                vector.length,
            );
            cesiumVector.polyline.positions = [tail, head];
        }
    }

    private _drawLabels(labels: Array<LabelModel>): void {
        // remove any extraneous labels
        while (this._labels.length > labels.length) {
            dropLabel(this._labels);
        }
        // create any missing labels
        while (this._labels.length < labels.length) {
            const label = new CesiumTransientLabel(this._extraLabelCollection, {
                eyeOffset: getIndicatorLabelEyeOffset(this.assetIcon.eyeOffset),
                font: `600 ${ConfigService.cesiumIndicatorLabelPixelSize}px Helvetica`,
                show: shouldShowIndicatorLabel(this.isShowingIcon(), this._isSelected),
                fillColor: Cesium.Color.WHITE,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                position: this.position as unknown as Cesium.PositionProperty,
                translucencyByDistance: INDICATOR_SCALAR,
                scaleByDistance: INDICATOR_SCALAR,
                pixelOffsetScaleByDistance: INDICATOR_SCALAR,
            });
            this._labels.push({
                label,
            });
        }

        // update our labels
        for (let i = 0; i < this._labels.length; i++) {
            const updatedLabel = labels[i];
            const { label } = this._labels[i];
            label.text = updatedLabel.text;
            label.show = this.isShowingIcon() && !updatedLabel.text.includes('null');
            label.pixelOffset = getLabelPixelOffset(this.label.pixelOffset, i + 1);
            label.horizontalOrigin = Cesium.HorizontalOrigin.CENTER;
        }
    }

    _createSelection() {
        this.selection.show = true;
        this.selection = this._selectionCollection.add(this.selection);

        const SELECTED = 1; // Offset it by one above all other assets.
        // Commented out because this breaks selectionService functionality.
        // this._offsetManager.set(this.id, this, OffsetManager.ZINDEX.Asset, SELECTED);
        // This simply puts the selection ring above everything, this should be removed when the above is fixed.
        this._offsetManager.set(-this.id, this.selection, OffsetManager.ZINDEX.Asset, SELECTED);
    }

    _destroySelection() {
        this.selection.show = false;
        // Copy values that we need.
        const newSelection = copyBillboard(this.selection);
        // Remove from Cesium
        this._selectionCollection.remove(this.selection);
        // Refresh our reference with a new selection object.
        this.selection = newSelection;

        // Commented out because this breaks selectionService functionality.
        // this._offsetManager.set(this.id, this, OffsetManager.ZINDEX.Asset);
        // This simply puts the selection ring above everything, this should be removed when the above is fixed.
        this._offsetManager.delete(-this.id);
    }
}

// Function to help check equality for cesium things
function cesiumEqualityChecker(a, b) {
    if (!a && !b) {
        return true;
    }

    if (a && b && a.equals(b)) {
        return true;
    }
    return false;
}

function shouldShowIndicatorLabel(isAssetShowing: boolean, isAssetSelected: boolean): boolean {
    return isAssetShowing && isAssetSelected;
}

function shouldShowIndicatorBillboard(isAssetShowing: boolean): boolean {
    return isAssetShowing;
}

function getIndicatorLabelEyeOffset(assetEyeOffset: Cesium.Cartesian3): Cesium.Cartesian3 {
    // Increase our Z out from the world, this will put it closer to the camera and above entities.
    return new Cesium.Cartesian3(0, 0, assetEyeOffset.z - 1);
}

function getIndicatorBillboardEyeOffset(assetEyeOffset: Cesium.Cartesian3): Cesium.Cartesian3 {
    // Increase our Z out from the world, this will put it closer to the camera and above entities.
    return new Cesium.Cartesian3(0, 0, assetEyeOffset.z - 10);
}

function getLabelEyeOffset(assetEyeOffset: Cesium.Cartesian3): Cesium.Cartesian3 {
    // Increase our Z out from the world, this will put it closer to the camera and above entities.
    return new Cesium.Cartesian3(assetEyeOffset.x, assetEyeOffset.y, assetEyeOffset.z - 1);
}

function getLabelPixelOffset(assetPixelOffset: Cesium.Cartesian2, index): Cesium.Cartesian2 {
    return new Cesium.Cartesian2(
        assetPixelOffset.x,
        assetPixelOffset.y + ConfigService.cesiumAssetLabelPixelSize * (1 * index + 0.3),
    );
}

/**
 *
 * @param count The number of positions to generate
 * @param itemsPerLayer The maximum number of positions that should exist at a single radius level
 */
function calculatePositionsAroundCircle(
    count: number,
    itemsPerLayer: number = 8,
): Array<{ radians: number; layer: number }> {
    // TODO memoize this function
    const positions = [];
    let i = -1;
    while (++i < count) {
        if (i < itemsPerLayer) {
            const radians = Math.PI - (i * Math.PI) / 4;
            positions.push({ radians, layer: 1 });
        } else if (i < itemsPerLayer * 2) {
            const radians = Math.PI - Math.PI / 8 - (i * Math.PI) / 4;
            positions.push({ radians, layer: 2 });
        } else {
            console.warn(`too many items! we're going to stack them on top of each other!`);
            // TODO oops quick hack sorry
            positions.push(last(positions));
        }
    }
    return positions;
}

// This value is close to 1 which is sin(PI/2), i.e. 90 degrees from the origin on the unit circle
const TOP_OR_BOTTOM_CHECK_VALUE = 0.99;
const TOP_OR_BOTTOM_LABEL_RADIUS_ADJUSTMENT = 7;
// The strange scaling value here comes from when the asset pixel size was hard set to
// 36. By providing this scaling factor we can now adapt to different pixel sizes.
const BILLBOARD_WIDTH = (15 / 36) * ASSET_PIXEL_SIZE;
const BILLBOARD_HEIGHT = BILLBOARD_WIDTH;
const ASSET_TO_INDICATOR_SPACE = (10 / 36) * ASSET_PIXEL_SIZE;
const INDICATOR_LAYER_RING_SIZE = (30 / 36) * ASSET_PIXEL_SIZE;

function getBillboardOffset(radians: number, layer: number): Cesium.Cartesian2 {
    const radius =
        BILLBOARD_WIDTH + ASSET_TO_INDICATOR_SPACE + (layer - 1) * INDICATOR_LAYER_RING_SIZE;
    const heightMod = -radius * Math.sin(radians); // We flip this as our y coordinate is flipped
    const lengthMod = radius * Math.cos(radians);
    return new Cesium.Cartesian2(lengthMod, heightMod);
}

function getCustomSpacingOffset(radians: number, layer: number, space: number): Cesium.Cartesian2 {
    const radius = BILLBOARD_WIDTH + space + (layer - 1) * INDICATOR_LAYER_RING_SIZE;
    const heightMod = -radius * Math.sin(radians); // We flip this as our y coordinate is flipped
    const lengthMod = radius * Math.cos(radians);
    return new Cesium.Cartesian2(lengthMod, heightMod);
}

function getLabelOffset(radians: number, layer: number, space: number): Cesium.Cartesian2 {
    const radius = BILLBOARD_WIDTH + space + (layer - 1) * INDICATOR_LAYER_RING_SIZE;
    const y = Math.sin(radians);
    let radiusAdjustment = 0;
    if (Math.abs(y) > TOP_OR_BOTTOM_CHECK_VALUE) {
        // if its the top or bottom item, increase the radius by this amount for the
        // label height position
        radiusAdjustment = TOP_OR_BOTTOM_LABEL_RADIUS_ADJUSTMENT;
    }
    const heightMod = -(radius + radiusAdjustment) * y; // We flip this as our y coordinate is flipped
    const lengthMod = radius * Math.cos(radians);
    return new Cesium.Cartesian2(lengthMod, heightMod);
}

/**
 * Used to position text nicely for an indicator
 * @param radians
 * @returns {Number|string|number}
 */
function getHorizontalOrigin(radians) {
    const x = Math.cos(radians);
    if (Math.abs(x) < 0.01) {
        // This is on the top or bottom (i.e., the center) of the circle
        return Cesium.HorizontalOrigin.CENTER;
    } else if (x > 0) {
        // This is on the right of center of the circle
        return Cesium.HorizontalOrigin.LEFT;
    } else {
        // This is on the left of center of the circle
        return Cesium.HorizontalOrigin.RIGHT;
    }
}

/**
 * Used to position text nicely for an indicator
 * @param radians
 * @returns {*}
 */
function getVerticalOrigin(radians: number) {
    const y = Math.sin(radians);
    if (Math.abs(y) < TOP_OR_BOTTOM_CHECK_VALUE) {
        // This is not on the very top or bottom of the circle
        return Cesium.VerticalOrigin.CENTER;
    } else if (y > 0) {
        // This is on the very top of the circle
        return Cesium.VerticalOrigin.BOTTOM;
    } else {
        // This is on the very bottom of the circle
        return Cesium.VerticalOrigin.TOP;
    }
}

function createArrowHead(
    arrowTailPosition: Cesium.Cartesian3,
    headingPitchRoll: Cesium.HeadingPitchRoll,
    magnitude: number,
): Cesium.Cartesian3 {
    // copied from https://stackoverflow.com/a/58108708
    if (!arrowTailPosition) {
        return null;
    }
    magnitude = magnitude || 0;
    headingPitchRoll.heading = headingPitchRoll.heading || 0;
    headingPitchRoll.pitch = headingPitchRoll.pitch || 0;
    headingPitchRoll.roll = headingPitchRoll.roll || 0;
    headingPitchRoll.heading = headingPitchRoll.heading - Cesium.Math.toRadians(90);
    const referenceFrame1 = Cesium.Transforms.headingPitchRollQuaternion(
        arrowTailPosition,
        headingPitchRoll,
    );
    const rotationMatrix = Cesium.Matrix3.fromQuaternion(referenceFrame1, new Cesium.Matrix3());
    const rotationScaled = Cesium.Matrix3.multiplyByVector(
        rotationMatrix,
        new Cesium.Cartesian3(magnitude, 0, 0),
        new Cesium.Cartesian3(),
    );
    return Cesium.Cartesian3.add(arrowTailPosition, rotationScaled, new Cesium.Cartesian3());
}

function dropVector(vectors: Array<CesiumVector>, polylineCollection: Cesium.PolylineCollection) {
    const { polyline } = vectors.pop();
    polylineCollection.remove(polyline);
}

function dropPolygon(polygons: Array<CesiumPolygon>) {
    const { zone } = polygons.pop();
    zone.destroy();
}

function dropConnectionLine(
    connectionLines: Array<CesiumConnectionLine>,
    polylineCollection: Cesium.PolylineCollection,
) {
    const { polyline } = connectionLines.pop();
    polylineCollection.remove(polyline);
}

function dropLine(lines: Array<CesiumLine>) {
    const { primitive } = lines.pop();
    primitive.destroy();
}

function dropIndicator(
    id: number,
    indicators: Array<CesiumIndicator>,
    assetIconCollection: Cesium.BillboardCollection,
    offsetManager: OffsetManager,
) {
    const indicatorId = id + 0.01 * indicators.length;
    const { label, billboard } = indicators.pop();
    assetIconCollection.remove(billboard);
    label.destroy();
    offsetManager.delete(indicatorId);
}

function dropLabel(labels: Array<CesiumLabel>) {
    const { label } = labels.pop();
    label.destroy();
}
